import { StaffUserRole } from "@gymflow/types";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import { FormInput, FormSelectInput } from "../../../../atoms";
import { FormImageInput } from "../../../../atoms/form/FormImageInput";
import { FormSelectLocations } from "../../../../atoms/form/selects/components/FormSelectLocations";
import { FormPhoneNumberInput } from "../../../../atoms/form/text/FormPhoneNumberInput";
import { UserIcon } from "../../../../atoms/icons/UserIcon";
import { roleOptions } from "./constants";
import { StaffFormValues } from "./types";

type StaffFormProps = {
  showEditingSelfRoleWarning?: boolean;
  formik: FormikProps<StaffFormValues>;
};

export const StaffForm = ({
  showEditingSelfRoleWarning,
  formik,
}: StaffFormProps) => {
  const values = formik?.values;
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 py-4">
      <FormImageInput<StaffFormValues>
        name="picture"
        title={t("page.settings.tab.staff.addStaff.form.imageLabel")}
        modalTitle={t("page.settings.tab.staff.addStaff.form.imageModalTitle")}
        placeholder={
          values.firstName ? (
            <div className="text-4xl font-semibold text-gray-700">
              {values.firstName[0].toUpperCase()}
            </div>
          ) : (
            <UserIcon className="size-9" pathClassName="stroke-gray-950" />
          )
        }
      />
      <FormInput<StaffFormValues>
        type="email"
        name="email"
        placeholder={t(
          "page.settings.tab.staff.addStaff.form.emailPlaceholder",
        )}
        label={t("page.settings.tab.staff.addStaff.form.emailLabel")}
        isRequired
      />
      <div className="mt-4 flex gap-x-4">
        <FormInput<StaffFormValues>
          type="text"
          name="firstName"
          placeholder={t(
            "page.settings.tab.staff.addStaff.form.firstNamePlaceholder",
          )}
          label={t("page.settings.tab.staff.addStaff.form.firstNameLabel")}
          isRequired
        />
        <FormInput<StaffFormValues>
          type="text"
          name="lastName"
          placeholder={t(
            "page.settings.tab.staff.addStaff.form.lastNamePlaceholder",
          )}
          label={t("page.settings.tab.staff.addStaff.form.lastNameLabel")}
          isRequired
        />
      </div>
      <FormPhoneNumberInput<StaffFormValues>
        name="mobileNumber"
        label={t("page.settings.tab.staff.addStaff.form.phoneLabel")}
        placeholder={t(
          "page.settings.tab.staff.addStaff.form.phonePlaceholder",
        )}
        className="w-full"
        isRequired
      />
      <FormSelectInput<StaffFormValues, StaffUserRole>
        name="role"
        label={t("page.settings.tab.staff.addStaff.form.roleLabel")}
        placeholder={t("page.settings.tab.staff.addStaff.form.rolePlaceholder")}
        options={roleOptions}
        isRequired
        dropPosition="top"
        warningMessage={
          showEditingSelfRoleWarning
            ? t("page.settings.tab.staff.addStaff.form.editingSelfRoleWarning")
            : undefined
        }
      />
      <FormSelectLocations<StaffFormValues>
        name="clubAccessibleList"
        label={t("page.settings.tab.staff.addStaff.form.locationsLabel")}
        placeholder={t(
          "page.settings.tab.staff.addStaff.form.locationsPlaceholder",
        )}
        isMulti
        withImageByDefault
        dropPosition="top"
        isRequired
      />
    </div>
  );
};
